import Button from "@/common/components/Button";
import { FileUploader } from "@/common/components/FileUploader";
import Tabs from "@/common/components/Tabs";
import { User2Icon } from "lucide-react";
import { useParams } from "react-router-dom";
import useAnpReport from "../../hooks/useAnpReport";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { formatDateRange, formatDateTime } from "@/common/utils/formatters";
import AnpPricesTable from "../../components/AnpPricesTable";
import { useApproveAnpReport } from "../../hooks/useApproveAnpReport";
import { useQueryClient } from "@tanstack/react-query";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { ShowHeader } from "@/common/components/ShowHeader";

const AnpReportsShow = () => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const { filterParams } = useFilterParams();

  const {
    data: anpReport,
    error: anpReportError = {} as any,
    refetch: refetchAnpReport,
  } = useAnpReport({ id });

  const { mutate: approveAnpReport, isPending: approveAnpReportIsPending } = useApproveAnpReport({
    id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anpReports'] });
      queryClient.invalidateQueries({ queryKey: ['anpReport', id] });
    },
  });

  const documentName = anpReport?.document
    ? (anpReport.document.filename || getFilenameFromUrl(anpReport.document.url))
    : '';

  return (
    <div className="flex flex-col gap-6">
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/anp-reports` }}
        title="Tabelas ANP"
        subtitle={documentName}
        buttons={(
          <>
            {anpReport && !anpReport?.approved_at && (
              <Button onClick={() => approveAnpReport()} isLoading={approveAnpReportIsPending}>
                Aprovar e efetivar
              </Button>
            )}
          </>
        )}
      />

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={anpReportError?.status}
        refetchFunction={refetchAnpReport}
      >
        <div className="flex justify-between">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="italic font-light text-xs">Nome da tabela</p>
              <p className="font-bold text-md">
                {documentName}
              </p>
            </div>

            <div className="flex gap-8">
              <div className="flex flex-col gap-1">
                <p className="italic font-light text-xs">Período de competência</p>
                <p className="font-bold text-md">
                  {anpReport?.initial_date && anpReport?.end_date
                    ? formatDateRange(
                        new Date(`${anpReport.initial_date}T00:00:00`),
                        new Date(`${anpReport.end_date}T00:00:00`)
                      )
                    : '--'}
                </p>
              </div>

              {anpReport?.approved_by && (
                <>
                  <div className="flex flex-col gap-1">
                    <p className="italic font-light text-xs">Aprovado por</p>

                    <div className="flex items-center gap-2">
                      <ImageWithPlaceholder
                        placeholderIcon={User2Icon}
                        url={anpReport.approved_by.profile_picture?.url}
                        className="w-6 h-6"
                        iconClassName="w-4 h-4"
                      />

                      <p className="font-bold text-md">{anpReport.approved_by.full_name}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <p className="italic font-light text-xs">Aprovado às</p>
                    <p className="font-bold text-md">
                      {anpReport.approved_at && formatDateTime(new Date(anpReport.approved_at))}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {anpReport?.document && (
            <div>
              <FileUploader
                className="w-48"
                disabled
                fileName={documentName}
                currentFileUrl={anpReport.document.url}
              />
            </div>
          )}
        </div>

        <Tabs
          tabsListClassName="w-full bg-transparent rounded-none"
          tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6"
          keepFilterParams
          filterParamsOnTabChange={{
            ...filterParams,
            page: undefined,
          }}
          tabsList={[
            {
              label: 'Municípios',
              value: '',
              body: <AnpPricesTable reportId={id} geoType="Geo::City" />,
            },
            {
              label: 'Estados',
              value: 'states',
              body: <AnpPricesTable reportId={id} geoType="Geo::State" />,
            },
            {
              label: 'Regiões',
              value: 'regions',
              body: <AnpPricesTable reportId={id} geoType="Geo::Region" />,
            },
            {
              label: 'Brasil',
              value: 'country',
              body: <AnpPricesTable reportId={id} geoType="Geo::Country" />,
            },
          ]}
        />
      </ErrorMessageWrapper>
    </div>
  );
};

export default AnpReportsShow;
